import React, { useCallback, useContext, useState } from "react";
import StepContainer from "./StepContainer";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";
import logo from "../../../Header/nav-logo.png";
import Button from "@mui/material/Button";
import { OnboardingContext } from "../../../../store/OnboardingContext";
import {
  bodyTextResetCodeSx,
  bodyTextSx,
  formFieldSx,
  submitSx,
  successBoxHeaderSx,
  successBoxSx,
  SuccessLogo,
} from "../SignUpForm.styles";
import { validateGiftcardCode } from "../../../../api/client";
import GiftcardResponse from "../../../../api/responses/GiftcardResponse";
import { APPLICATION_SOURCE } from "@booyaltd/core";
import { useLocation, useParams } from "react-router-dom";

/**
 * PM5 T2 (money path, batch "onboarding") — converted from MUI v4 to MUI v5+
 * (`@mui/material`). See docs/mui-conversion-guide.md.
 *
 * **SUBSCRIPTIONS / PAYMENTS ESCALATION CLASS.** Giftcard redemption — the
 * step that validates a code with the API and hands it to the onboarding
 * journey as paid entitlement. Styling-only conversion: `validateGiftcardCode`,
 * the code-vs-input equality guard and the `initialise({...})` payload are
 * untouched (and pinned by SignUpStepGiftcard.test.tsx).
 */

/**
 * The `<Grid item xs={12} sm={12}>` wrapping the success panel and the buttons
 * sits OUTSIDE any `<Grid container>` — its parent is StepContainer.Body's Box.
 * In v4 it still emitted the item's `flex-basis` / `max-width` percentages; in
 * v9 a Grid with no container falls back to full width. Guide §2: port an
 * orphan item as a Box carrying the literal v4 declarations.
 */
const orphanGridItemSx: SxProps<Theme> = {
  boxSizing: "border-box",
  margin: 0,
  flexGrow: 0,
  flexBasis: "100%",
  maxWidth: "100%",
};

const SignUpStepGiftcard = () => {
  const { code: urlCode } = useParams<{ code?: string }>();
  const location = useLocation();
  const { loading, error, runWithLoader, initialise } =
    useContext(OnboardingContext);
  const [complete, setComplete] = useState(false);
  const [codeInputValue, setCodeInputValue] = useState(urlCode || "");
  const [code, setCode] = useState<GiftcardResponse>();

  const codeDurationString = code
    ? `${code.entitlementPeriodValue} ${code.entitlementPeriodUnit}${
        code.entitlementPeriodValue > 1 ? "s" : ""
      }`
    : "";

  const handleGiftcardInput = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setCodeInputValue(evt.target.value.toUpperCase().trim());

      if (complete) {
        setComplete(false);
      }
    },
    [complete],
  );

  const onCheckClicked = useCallback(
    (evt: React.MouseEvent<HTMLButtonElement>) => {
      evt.preventDefault();

      if (!codeInputValue) {
        return;
      }

      runWithLoader(async () => {
        await validateGiftcardCode(codeInputValue).then((giftcard) => {
          setCodeInputValue(giftcard.giftcardCode);
          setCode(giftcard);
        });
      });
    },
    [codeInputValue, runWithLoader],
  );

  const onResetClicked = useCallback(() => {
    setCode(undefined);
    setCodeInputValue("");
  }, []);

  const onNextClicked = useCallback(() => {
    if (code && code.giftcardCode === codeInputValue) {
      initialise({
        route: "giftcard",
        source: APPLICATION_SOURCE.GIFTCARD,
        code: code.giftcardCode,
        page: location.pathname,
      });
    }
  }, [code, codeInputValue, initialise, location.pathname]);

  return (
    <StepContainer>
      <StepContainer.Title title={complete ? "Confirm Code" : "Enter Code"} />
      <StepContainer.Body>
        <>
          {!complete && (
            <Grid container>
              <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                <Typography variant="body2" sx={bodyTextSx} align="center">
                  Enter the 16 character code from your Giftcard to start
                  enjoying FarmFLiX.
                </Typography>
                {/* PARITY RESTORE (guide §2, "TextField default variant"): v4
                    defaulted to "standard"; v5+ defaults to "outlined", which
                    would render a different control and bypass themeV5's
                    `MuiInput.underline` overrides. `inputProps` was removed in
                    the v9 line — `slotProps.htmlInput` is the same object on
                    the same <input>. */}
                <TextField
                  name="giftcard"
                  variant="standard"
                  slotProps={{
                    htmlInput: {
                      min: 0,
                      style: {
                        textAlign: "center",
                        fontSize: "20px",
                        fontWeight: "bold",
                      },
                    },
                  }}
                  sx={formFieldSx}
                  fullWidth={true}
                  type="text"
                  autoCapitalize="true"
                  value={codeInputValue}
                  onChange={handleGiftcardInput}
                  disabled={loading}
                  error={!!error}
                  helperText={"E.g. xxxx-xxxx-xxxx-xxxx"}
                />
              </Grid>
            </Grid>
          )}

          <Box sx={orphanGridItemSx} style={{ textAlign: "center" }}>
            {code && (
              <>
                <div>
                  <Box sx={successBoxSx}>
                    <SuccessLogo src={logo} alt="" />
                    <Typography
                      variant="body2"
                      sx={bodyTextSx}
                      style={{
                        marginTop: "10px",
                        fontWeight: 500,
                        fontSize: "1rem",
                        color: "#171717",
                      }}
                    >
                      This Giftcard will give you access to FarmFLiX for:
                    </Typography>
                    <Typography sx={successBoxHeaderSx}>
                      {codeDurationString}
                    </Typography>
                  </Box>
                </div>
                <Grid container>
                  <Grid
                    size={{ xs: 12, sm: 12 }}
                    style={{ textAlign: "center" }}
                  >
                    <Typography
                      sx={bodyTextResetCodeSx}
                      variant="body2"
                      onClick={onResetClicked}
                    >
                      Wrong Code? Reset Now
                    </Typography>
                  </Grid>
                </Grid>
              </>
            )}

            {(!code || code.giftcardCode !== codeInputValue) && (
              <Button
                name="check"
                size="medium"
                type="button"
                sx={submitSx}
                disabled={loading || !codeInputValue}
                onClick={onCheckClicked}
              >
                Check Giftcard
              </Button>
            )}

            {code && code.giftcardCode === codeInputValue && (
              <Button
                name="next"
                size="medium"
                type="button"
                sx={submitSx}
                disabled={loading || !codeInputValue}
                onClick={onNextClicked}
              >
                Next
              </Button>
            )}
          </Box>
        </>
      </StepContainer.Body>
      <StepContainer.Actions />
    </StepContainer>
  );
};

export default SignUpStepGiftcard;
